import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import CKEditor5 from 'components/CkEditor5';
import dayjs from 'dayjs';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';

type AmendmentEntry = {
  id?: number | string;
  datetime?: string;
  createdAt?: string;
  summary?: string;
  createdBy?: string;
};

type AmendmentSectionProps = {
  isLoading: boolean;
  setIsLoading: (val: boolean) => void;
  amendments: AmendmentEntry[];
  getInitialData: () => void;
  isPastBooking: boolean;
  setMessage: any;
};

export type AmendmentSectionHandle = {
  openComposer: () => void;
};

const formatAmendmentMeta = (entry: AmendmentEntry) => {
  const at = dayjs(entry.datetime || entry.createdAt);
  const parts: string[] = [];
  if (at.isValid()) {
    parts.push(at.format('D MMM YYYY'));
    parts.push(at.format('hh:mm A'));
  }
  const author = String(entry.createdBy || '').trim() || 'anonymous';
  parts.push(strings.beo_amendment_added_by.replace('{name}', author));
  return parts.join(' . ');
};

export const AmendmentSection = forwardRef<AmendmentSectionHandle, AmendmentSectionProps>(
  ({ isLoading, setIsLoading, getInitialData, amendments, isPastBooking, setMessage }, ref) => {
    const theme = useTheme();
    const { id } = useParams() as any;
    const rootRef = useRef<HTMLDivElement>(null);
    const [newData, setNewData] = useState<any>('');
    const [error, setError] = useState<boolean>(false);
    const [expanded, setExpanded] = useState(() => !isPastBooking && !(amendments?.length > 0));

    useImperativeHandle(ref, () => ({
      openComposer: () => {
        if (isPastBooking) return;
        setExpanded(true);
        window.setTimeout(() => {
          rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 160);
      },
    }));

    const handleUpdate = async () => {
      try {
        if (newData === '') {
          setError(true);
          return false;
        }
        const body = new FormData();
        body.append('amendment', newData || '');
        setIsLoading(true);
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
        await axiosInstance.post(URLS.addBeoAmendments(id), body, config);
        setIsLoading(false);
        getInitialData();
        setNewData('');
        setError(false);
      } catch (error: any) {
        if (error.response) {
          setMessage({
            display: true,
            severity: 'error',
            message: error.response.data?.error_description ?? error.response.data.message,
          });
        } else {
          setMessage({
            display: true,
            severity: 'error',
            message: error.message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    const orderedAmendments = useMemo(
      () =>
        [...(amendments ?? [])].sort(
          (a, b) => dayjs(a.datetime || a.createdAt).valueOf() - dayjs(b.datetime || b.createdAt).valueOf(),
        ),
      [amendments],
    );

    return (
      <Box ref={rootRef} id="beo-amendment-section" sx={{ width: '100%', mt: 1 }}>
        <Accordion
          expanded={expanded}
          onChange={(_e, next) => {
            if (isPastBooking) return;
            setExpanded(next);
          }}
          sx={{ width: '100%', mb: 1 }}
          disabled={isPastBooking}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="beo-amendment-content"
            id="beo-amendment-header"
            sx={{ backgroundColor: theme.palette.secondary.main }}>
            <Typography sx={{ fontWeight: 500 }} color={theme.palette.primary.main}>
              {strings.add_ammendment}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CKEditor5
                  disabled={isPastBooking}
                  label={''}
                  textChange={(value: any) => {
                    setNewData(value);
                    if (value !== '') setError(false);
                  }}
                  value={newData}
                />
                {error && (
                  <Typography color="red" fontSize={'14px'} fontWeight={500}>
                    {strings.requiredText}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <br />
            <Button
              variant="contained"
              onClick={() => handleUpdate()}
              sx={{ textTransform: 'capitalize' }}
              disabled={isLoading || isPastBooking}>
              {strings.add_ammendment}
            </Button>
          </AccordionDetails>
        </Accordion>
        {orderedAmendments.length > 0 && (
          <Box sx={{ pt: 1, pb: 0.5 }}>
            {orderedAmendments.map((entry, index) => {
              const isLast = index === orderedAmendments.length - 1;
              return (
                <Box
                  key={entry.id ?? `${entry.datetime}-${index}`}
                  sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 22,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: '22px',
                        textAlign: 'center',
                        flexShrink: 0,
                      }}>
                      {index + 1}
                    </Box>
                    {!isLast && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          bgcolor: 'divider',
                          my: 0.5,
                          borderRadius: 1,
                          minHeight: 12,
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0.5 : 2 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'text.secondary',
                        lineHeight: 1.4,
                      }}>
                      {formatAmendmentMeta(entry)}
                    </Typography>
                    {entry.summary ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          fontSize: 13,
                          lineHeight: 1.5,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          '& p': { m: 0, mb: 0.75 },
                          '& p:last-child': { mb: 0 },
                          '& ul, & ol': { my: 0.5, pl: 2.5 },
                        }}
                        dangerouslySetInnerHTML={{ __html: entry.summary }}
                      />
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  },
);

AmendmentSection.displayName = 'AmendmentSection';
